/**
 * sirion-ui integration adapter for SirionUserTimeTracker (End User web surface).
 */
(function (global) {
    'use strict';

    var ENCRYPTION_KEY = 'SirEncKey@01';
    var ENCRYPTION_KEY_ID = '2026-v1';
    var startPromise = null;

    function warn(message) {
        if (typeof console !== 'undefined' && console.warn) {
            console.warn('[UserTimeTrackerAdapter] ' + message);
        }
    }

    function getClientAlias() {
        try {
            var hostname = global.location && global.location.hostname;
            if (!hostname) {
                return '';
            }
            var labels = hostname.split('.');
            // Tenant subdomain only (e.g. dxcuk from dxcuk.sirioncloud.com), not host/path
            if (labels.length >= 3 && labels[0] !== 'www') {
                return labels[0];
            }
            return '';
        } catch (e) {
            return '';
        }
    }

    function getSessionId() {
        try {
            var fromStorage = global.localStorage && global.localStorage.getItem('_csrf_token');
            if (fromStorage) {
                return fromStorage;
            }
            if (global.token) {
                return String(global.token);
            }
            if (typeof document !== 'undefined') {
                var meta = document.querySelector('meta[name="_csrf_token"]');
                if (meta && meta.getAttribute('content')) {
                    return meta.getAttribute('content');
                }
            }
            return '';
        } catch (e) {
            return '';
        }
    }

    function getSideLayoutData() {
        try {
            var raw = global.localStorage && global.localStorage.getItem('sideLayoutData');
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            return null;
        }
    }

    function shouldSkipInit() {
        if (!global.heapConfig || !global.heapConfig.userId) {
            return true;
        }
        if (typeof global.location !== 'undefined' && global.location.href.indexOf('authToken') > -1) {
            return true;
        }
        if (typeof global.SirionUserTimeTracker === 'undefined') {
            return true;
        }
        return false;
    }

    function fetchAccountUserInfo() {
        return new Promise(function (resolve) {
            var sideLayout = getSideLayoutData();
            var fallbackEmail = sideLayout &&
                sideLayout.data &&
                sideLayout.data.headerBar &&
                sideLayout.data.headerBar.userData &&
                sideLayout.data.headerBar.userData.userEmail;

            var fallbackUserName = sideLayout &&
                sideLayout.data &&
                sideLayout.data.headerBar &&
                sideLayout.data.headerBar.userData &&
                sideLayout.data.headerBar.userData.userName;

            var fallback = {
                firstName: '',
                lastName: '',
                email: fallbackEmail || '',
                loginId: ''
            };

            if (fallbackUserName) {
                var parts = String(fallbackUserName).trim().split(/\s+/);
                fallback.firstName = parts[0] || '';
                fallback.lastName = parts.slice(1).join(' ') || parts[0] || '';
            }

            if (typeof fetch !== 'function') {
                resolve(fallback);
                return;
            }

            var base = global.appName || '/ux';
            var url = base + '/account/info';
            if (typeof global.addtStamp === 'function') {
                url = global.addtStamp(url);
            }

            fetch(url, {
                method: 'GET',
                credentials: 'same-origin',
                headers: { 'Accept': 'application/json' }
            })
                .then(function (response) {
                    if (!response.ok) {
                        throw new Error('account/info failed');
                    }
                    return response.json();
                })
                .then(function (data) {
                    var user = (Array.isArray(data) && data[0] && data[0].user) ||
                        (data && data.body && data.body.user) ||
                        (data && data.user) ||
                        null;
                    if (!user) {
                        warn('account/info returned no user — using sideLayout fallback');
                        resolve(fallback);
                        return;
                    }
                    resolve({
                        firstName: user.firstName || fallback.firstName || '',
                        lastName: user.lastName || fallback.lastName || '',
                        email: user.email || fallback.email || '',
                        loginId: user.loginId || ''
                    });
                })
                .catch(function () {
                    warn('account/info unavailable — using sideLayout fallback');
                    resolve(fallback);
                });
        });
    }

    function buildTrackerConfig(accountUser) {
        if (global.EndUserWebModuleResolver && typeof global.EndUserWebModuleResolver.init === 'function') {
            global.EndUserWebModuleResolver.init({ sideLayoutData: getSideLayoutData() });
        }

        return {
            sessionType: 'End User',
            encryptionKey: ENCRYPTION_KEY,
            encryptionKeyId: ENCRYPTION_KEY_ID,
            storage: global.sessionStorage,
            trackFn: function (eventType, payload) {
                if (eventType !== 'session_start' && eventType !== 'active_time') {
                    warn('Unexpected event type: ' + eventType);
                    return;
                }
                payload['Event Type'] = eventType;
                console.log('[SirionUTT → Heap]', eventType, payload);
                if (typeof global.heap !== 'undefined' && typeof global.heap.track === 'function') {
                    global.heap.track(eventType, payload);
                    return;
                }
                console.warn('[SirionUTT → Heap] heap.track unavailable — event logged above only:', eventType);
            },
            getContext: function () {
                var sideLayout = getSideLayoutData();
                var env = sideLayout &&
                    sideLayout.data &&
                    sideLayout.data.rightSideBar &&
                    sideLayout.data.rightSideBar.springProperties &&
                    sideLayout.data.rightSideBar.springProperties.sirionEnvDetails;
                var timezone = global.currTimeZoneString || '';
                return {
                    userId: global.heapConfig && global.heapConfig.userId,
                    clientId: global.heapConfig && global.heapConfig.clientId,
                    clientAlias: getClientAlias(),
                    sessionId: getSessionId(),
                    environment: env || '',
                    userClassification: global.heapConfig && global.heapConfig.userClassification,
                    userType: global.heapConfig && global.heapConfig.userPermissions,
                    timezone: timezone,
                    tenantTimezone: timezone
                };
            },
            getPii: function () {
                return accountUser;
            },
            moduleResolver: function () {
                if (global.EndUserWebModuleResolver && typeof global.EndUserWebModuleResolver.getModule === 'function') {
                    return global.EndUserWebModuleResolver.getModule();
                }
                return { module: 'Others', moduleTranslated: 'Others' };
            },
            isNavHidden: function () {
                if (global.EndUserWebModuleResolver && typeof global.EndUserWebModuleResolver.isNavHidden === 'function') {
                    return global.EndUserWebModuleResolver.isNavHidden();
                }
                return false;
            }
        };
    }

    function startUserTimeTracker() {
        if (shouldSkipInit()) {
            return Promise.resolve();
        }
        if (global.SirionUserTimeTracker.isRunning && global.SirionUserTimeTracker.isRunning()) {
            return Promise.resolve();
        }
        if (startPromise) {
            return startPromise;
        }

        startPromise = fetchAccountUserInfo()
            .then(function (accountUser) {
                return global.SirionUserTimeTracker.init(buildTrackerConfig(accountUser));
            })
            .finally(function () {
                startPromise = null;
            });

        return startPromise;
    }

    function stopUserTimeTracker() {
        startPromise = null;
        if (global.EndUserWebModuleResolver && typeof global.EndUserWebModuleResolver.destroy === 'function') {
            global.EndUserWebModuleResolver.destroy();
        }
        if (global.SirionUserTimeTracker && typeof global.SirionUserTimeTracker.destroy === 'function') {
            global.SirionUserTimeTracker.destroy();
        }
    }

    global.startUserTimeTracker = startUserTimeTracker;
    global.stopUserTimeTracker = stopUserTimeTracker;
})(typeof window !== 'undefined' ? window : global);
